import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import { Severity, valuesExist } from "../scenarioValidator";
import type { CorrelationValidationGenerator } from "./correlationChecks";

export const correlationsAtPumpDischargeChecks: CorrelationValidationGenerator =
  (correlationLimits, correlationField, { calcs }) => {
    const dischargeStageCalculations =
      calcs.pumpCalculations.individual.value[
        calcs.descendingOrderPumps.value[0]?.id
      ]?.dischargeStageCalculations;

    if (!dischargeStageCalculations) return [];

    const dischargeFluidProperties =
      dischargeStageCalculations.dischargeFluidPropertiesContext
        .fluidProperties;

    return [
      {
        name: `${correlationField}_oil_gravity_pump_discharge`,
        message: `API Out of Range @ Pump Discharge`,
        severity: Severity.warn,
        isActive: valuesExist([
          correlationLimits.MIN_API,
          correlationLimits.MAX_API,
          calcs.basicData.value.oilAPI
        ]),
        isInvalid:
          calcs.basicData.value.oilAPI > correlationLimits.MAX_API ||
          calcs.basicData.value.oilAPI < correlationLimits.MIN_API,
        rulePlacement: EquipmentCalculatorSectionKey.PUMP
      },
      {
        name: `${correlationField}_gas_specific_gravity_pump_discharge`,
        message: `Gas Specific Gravity Out of Range @ Pump Discharge`,
        severity: Severity.warn,
        isActive: valuesExist([
          correlationLimits.MIN_GAS_SG,
          correlationLimits.MAX_GAS_SG,
          calcs.basicData.value.gasSpecificGravity
        ]),
        isInvalid:
          calcs.basicData.value.gasSpecificGravity >
            correlationLimits.MAX_GAS_SG ||
          calcs.basicData.value.gasSpecificGravity <
            correlationLimits.MIN_GAS_SG,
        rulePlacement: EquipmentCalculatorSectionKey.PUMP
      },
      {
        name: `${correlationField}_temperature_pump_discharge`,
        message: `Temperature Out of Range @ Pump Discharge`,
        severity: Severity.warn,
        isActive: valuesExist([
          correlationLimits.MIN_TEMP,
          correlationLimits.MAX_TEMP,
          calcs.pumpCalculations.aggregate.dischargeTemperatureF.value
        ]),
        isInvalid:
          calcs.pumpCalculations.aggregate.dischargeTemperatureF.value >
            correlationLimits.MAX_TEMP ||
          calcs.pumpCalculations.aggregate.dischargeTemperatureF.value <
            correlationLimits.MIN_TEMP,
        rulePlacement: EquipmentCalculatorSectionKey.PUMP
      },
      {
        name: `${correlationField}_pressure_pump_discharge`,
        message: `Pressure Out of Range @ Pump Discharge`,
        severity: Severity.warn,
        isActive: valuesExist([
          correlationLimits.MIN_PRESS,
          correlationLimits.MAX_PRESS,
          calcs.pumpCalculations.aggregate.producedDischargePressurePSIA.value
        ]),
        isInvalid:
          calcs.pumpCalculations.aggregate.producedDischargePressurePSIA.value >
            correlationLimits.MAX_PRESS ||
          calcs.pumpCalculations.aggregate.producedDischargePressurePSIA.value <
            correlationLimits.MIN_PRESS,
        rulePlacement: EquipmentCalculatorSectionKey.PUMP
      },
      {
        name: `${correlationField}_oil_solution_gas_pump_discharge`,
        message: `Oil Solution Gas Out of Range @ Pump Discharge`,
        severity: Severity.warn,
        isActive: valuesExist([
          correlationLimits.MIN_RSO,
          correlationLimits.MAX_RSO,
          dischargeFluidProperties.oilPhaseGasSolubilitySCFSTB
        ]),
        isInvalid:
          dischargeFluidProperties.oilPhaseGasSolubilitySCFSTB >
            correlationLimits.MAX_RSO ||
          dischargeFluidProperties.oilPhaseGasSolubilitySCFSTB <
            correlationLimits.MIN_RSO,
        rulePlacement: EquipmentCalculatorSectionKey.PUMP
      },
      {
        name: `${correlationField}_bubblepoint_pressure_pump_discharge`,
        message: `Bubblepoint Pressure Out of Range @ Pump Discharge`,
        severity: Severity.warn,
        isActive: valuesExist([
          correlationLimits.MIN_PB,
          correlationLimits.MAX_PB,
          dischargeFluidProperties.bubblePointPressurePSIA
        ]),
        isInvalid:
          dischargeFluidProperties.bubblePointPressurePSIA >
            correlationLimits.MAX_PB ||
          dischargeFluidProperties.bubblePointPressurePSIA <
            correlationLimits.MIN_PB,
        rulePlacement: EquipmentCalculatorSectionKey.PUMP
      },
      {
        name: `${correlationField}_oil_formation_volume_factor_pump_discharge`,
        message: `Oil Formation Volume Factor Out of Range @ Pump Discharge`,
        severity: Severity.warn,
        isActive: valuesExist([
          correlationLimits.MIN_BO,
          correlationLimits.MAX_BO,
          dischargeFluidProperties.oilVolumeFactorBBLSTB
        ]),
        isInvalid:
          dischargeFluidProperties.oilVolumeFactorBBLSTB >
            correlationLimits.MAX_BO ||
          dischargeFluidProperties.oilVolumeFactorBBLSTB <
            correlationLimits.MIN_BO,
        rulePlacement: EquipmentCalculatorSectionKey.PUMP
      },
      {
        name: `${correlationField}_dead_oil_viscosity_pump_discharge`,
        message: `Dead Oil Viscosity Out of Range @ Pump Discharge`,
        severity: Severity.warn,
        isActive: valuesExist([
          correlationLimits.MIN_U_OD,
          correlationLimits.MAX_U_OD,
          dischargeFluidProperties.deadOilViscosityCP
        ]),
        isInvalid:
          dischargeFluidProperties.deadOilViscosityCP >
            correlationLimits.MAX_U_OD ||
          dischargeFluidProperties.deadOilViscosityCP <
            correlationLimits.MIN_U_OD,
        rulePlacement: EquipmentCalculatorSectionKey.PUMP
      },
      {
        name: `${correlationField}_live_oil_viscosity_pump_discharge`,
        message: `Live Oil Viscosity Out of Range @ Pump Discharge`,
        severity: Severity.warn,
        isActive: valuesExist([
          correlationLimits.MIN_U_O,
          correlationLimits.MAX_U_O,
          dischargeFluidProperties.oilViscosityCP
        ]),
        isInvalid:
          dischargeFluidProperties.oilViscosityCP > correlationLimits.MAX_U_O ||
          dischargeFluidProperties.oilViscosityCP < correlationLimits.MIN_U_O,
        rulePlacement: EquipmentCalculatorSectionKey.PUMP
      },
      {
        name: `${correlationField}_pseudo_reduced_pressure_pump_discharge`,
        message: `Pseudo-Reduced Pressure Out of Range @ Pump Discharge`,
        severity: Severity.warn,
        isActive: valuesExist([
          correlationLimits.MIN_PPR,
          correlationLimits.MAX_PPR,
          dischargeFluidProperties.psuedoCriticals.psuedoReducedPressure
        ]),
        isInvalid:
          dischargeFluidProperties.psuedoCriticals.psuedoReducedPressure >
            correlationLimits.MAX_PPR ||
          dischargeFluidProperties.psuedoCriticals.psuedoReducedPressure <
            correlationLimits.MIN_PPR,
        rulePlacement: EquipmentCalculatorSectionKey.PUMP
      },
      {
        name: `${correlationField}_pseudo_reduced_temperature_pump_discharge`,
        message: `Pseudo-Reduced Temperature Out of Range @ Pump Discharge`,
        severity: Severity.warn,
        isActive: valuesExist([
          correlationLimits.MIN_TPR,
          correlationLimits.MAX_TPR,
          dischargeFluidProperties.psuedoCriticals.psuedoReducedTemperature
        ]),
        isInvalid:
          dischargeFluidProperties.psuedoCriticals.psuedoReducedTemperature >
            correlationLimits.MAX_TPR ||
          dischargeFluidProperties.psuedoCriticals.psuedoReducedTemperature <
            correlationLimits.MIN_TPR,
        rulePlacement: EquipmentCalculatorSectionKey.PUMP
      }
    ];
  };
